import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Inbox } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useInviterCreatorRequestBatches } from '@/hooks/useInviterCreatorRequestBatches';
import { EmptyState } from '@/components/ui/empty-state';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import { Pill, type PillTone } from '@/components/ui/pill';
import { Tag, channelToneFor, channelLabel } from '@/components/ui/tag';
import { Combobox } from '@/components/ui/combobox';
import { Label } from '@/components/ui/label';
import { Pagination } from '@/components/ui/pagination';
import {
  DataTable,
  DataTableHeader,
  DataTableHeaderCell,
  DataTableRow,
} from '@/components/ui/data-table';
import { firstNameOf } from '@/lib/displayName';
import { teamLabel, titleCase } from '@/lib/labels';
import { useApi } from '@/hooks/useApi';
import { useMe } from '@/hooks/useMe';
import type {
  CreatorRequestBatchStatus,
  CreatorRequestComment,
  CreatorRequestType,
  OutreachNiche,
} from '@/api/types';

// ── Shared wording for the outreacher creator-request pages ─────────────────

/** Batch statuses, as outreachers read them. */
export const BATCH_STATUS_LABELS: Record<CreatorRequestBatchStatus, string> = {
  OPEN: 'Open',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
};
export const batchStatusLabel = (s: string): string =>
  BATCH_STATUS_LABELS[s as CreatorRequestBatchStatus] ?? titleCase(s);
export const batchStatusTone = (s: string): PillTone =>
  s === 'OPEN' ? 'info' : s === 'COMPLETED' ? 'success' : 'rejected';

/** Request type, as a word. */
export const REQUEST_TYPE_LABELS: Record<CreatorRequestType, string> = {
  CREATOR: 'Creator',
  NICHE: 'Niche',
};

/** Who wrote a comment: the campaign manager's side, an admin, or the outreach team. */
export function commentAuthorLabel(
  c: Pick<CreatorRequestComment, 'authorRole' | 'authorSide'>,
): string {
  if (c.authorRole === 'admin') return 'Admin';
  if (c.authorSide === 'OUTREACH') return 'Outreach team';
  return 'Campaign manager';
}

/** Niche code to display name (falls back to a title-cased code, never the raw code). */
export function useNicheNames(): (code: string) => string {
  const api = useApi();
  const niches = useQuery({
    queryKey: ['niches', 'all'],
    // Display lookup: hidden niches still name old requests.
    queryFn: () => api.get<OutreachNiche[]>('/portal/niches', { includeHidden: true }),
    staleTime: 5 * 60_000,
  });
  const data = niches.data;
  return useCallback(
    (code: string) => data?.find((n) => n.code === code)?.displayName ?? titleCase(code),
    [data],
  );
}

/** Team code to name, using the outreacher's own team display name when it matches. */
export function useTeamName(): (code: string) => string {
  const me = useMe();
  const team = me.data?.role === 'outreacher' ? me.data.team : null;
  return useCallback(
    (code: string) => teamLabel(code, team && team.code === code ? team.displayName : null),
    [team],
  );
}

const DEFAULT_STATUS = 'OPEN';
const STATUSES: CreatorRequestBatchStatus[] = ['OPEN', 'COMPLETED', 'CANCELLED'];
const PAGE_SIZE = 50;

function ProgressBar({
  checked,
  total,
  status,
}: {
  checked: number;
  total: number;
  status: CreatorRequestBatchStatus;
}) {
  const pct = total === 0 ? 0 : Math.round((checked / total) * 100);
  const fillClass =
    status === 'CANCELLED'
      ? 'bg-red-500'
      : pct === 100
        ? 'bg-emerald-500'
        : 'bg-blue-500';
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-24 overflow-hidden rounded-full bg-muted">
        <div className={`h-full ${fillClass}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-muted-foreground">
        {checked}/{total}
      </span>
    </div>
  );
}

/**
 * Panel content for the Batches sub-tab of the outreacher Requests page.
 * Owns its own filter + pagination state — reused by the tabbed Requests
 * surface. No page header (the parent renders one) so this drops cleanly
 * into a tab body.
 */
export function InviterCreatorRequestBatchesPanel(): JSX.Element {
  const [status, setStatus] = useState<CreatorRequestBatchStatus | ''>(DEFAULT_STATUS);
  const [page, setPage] = useState(1);

  const query = useInviterCreatorRequestBatches({
    ...(status ? { status } : {}),
    page,
    limit: PAGE_SIZE,
  });

  const rows = query.data?.data ?? [];
  const total = query.data?.meta?.total ?? 0;

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="grid gap-1.5">
          <Label>Status</Label>
          <Combobox
            value={status}
            onChange={(v) => {
              setStatus(v as CreatorRequestBatchStatus | '');
              setPage(1);
            }}
            options={STATUSES.map((s) => ({ value: s, label: BATCH_STATUS_LABELS[s] }))}
            allowAll
            allLabel="All statuses"
            placeholder="All statuses"
          />
        </div>
      </div>

      {query.isLoading ? (
        <TableSkeleton cols={['w-40', 'w-20', 'w-24', 'w-24', 'w-16']} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Inbox className="h-6 w-6" />}
          title="No batches yet."
          hint="Batches of creators that campaign managers send to your team land here."
        />
      ) : (
        <DataTable density="outreacher">
          <DataTableHeader>
            <tr>
              <DataTableHeaderCell>Batch</DataTableHeaderCell>
              <DataTableHeaderCell>Progress</DataTableHeaderCell>
              <DataTableHeaderCell>Channel</DataTableHeaderCell>
              <DataTableHeaderCell>Campaign manager</DataTableHeaderCell>
              <DataTableHeaderCell numeric>Age</DataTableHeaderCell>
            </tr>
          </DataTableHeader>
          <tbody>
            {rows.map((b) => (
              <DataTableRow key={b.id}>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/portal/creator-request-batches/${b.publicId}`}
                      className="font-medium text-foreground hover:underline"
                    >
                      {b.name}
                    </Link>
                    <Pill tone={batchStatusTone(b.status)}>{batchStatusLabel(b.status)}</Pill>
                  </div>
                </td>
                <td className="px-3 py-2" data-label="Progress">
                  <ProgressBar checked={b.checkedCount} total={b.itemCount} status={b.status} />
                </td>
                <td className="px-3 py-2" data-label="Channel">
                  {b.channel ? (
                    <Tag tone={channelToneFor(b.channel)}>{channelLabel(b.channel)}</Tag>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs text-muted-foreground" data-label="Campaign manager">
                  {firstNameOf(b.requesterName) || b.requesterName}
                </td>
                <td
                  className="px-3 py-2 text-right text-xs text-muted-foreground"
                  data-label="Age"
                >
                  {formatDistanceToNow(new Date(b.createdAt), { addSuffix: true })}
                </td>
              </DataTableRow>
            ))}
          </tbody>
        </DataTable>
      )}

      <Pagination page={page} limit={PAGE_SIZE} total={total} onPageChange={setPage} />
    </div>
  );
}
